import { useEffect, useState, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/AuthContext';
import { ThemeToggle } from '../ThemeToggle';
import { AdminSidebar } from './AdminSidebar';
import { AdminSidebarOpener } from './AdminSidebarOpener';

interface AdminShellProps extends PropsWithChildren {
  title?: string;
  subtitle?: string;
}

const SIDEBAR_HIDDEN_KEY = 'sense.admin.cloud.sidebarHidden';

export function AdminShell({ children, title, subtitle }: AdminShellProps): JSX.Element {
  const navigate = useNavigate();
  const { logout, session } = useAuth();
  const [sidebarHidden, setSidebarHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_HIDDEN_KEY, sidebarHidden ? '1' : '0');
    } catch {
      // ignore
    }
  }, [sidebarHidden]);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="admin-shell">
      {sidebarHidden ? (
        <AdminSidebarOpener onOpen={() => setSidebarHidden(false)} />
      ) : (
        <AdminSidebar onHide={() => setSidebarHidden(true)} />
      )}
      <div className="admin-shell__body">
        <header className="admin-shell__header">
          <div className="admin-shell__brand">
            <p className="admin-shell__product">SenseAI HQ</p>
            <h1>{title ?? 'Registry'}</h1>
            <p>{subtitle ?? session?.user.email ?? 'Staff console'}</p>
          </div>
          <div className="admin-shell__actions">
            <ThemeToggle />
            <button className="ghost-button" type="button" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </header>
        <main className="admin-shell__main">{children}</main>
      </div>
    </div>
  );
}
